import { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import api from '@/lib/api';

/**
 * Backend health badge — uptime / memory / pid + cold-start sparkline.
 * Extracted from DashboardPage to keep that file under 700 LOC.
 * Refreshes every 60 seconds via setInterval; cleans up on unmount.
 */
export function DeploymentHealthBadge() {
  const [state, setState] = useState({ loading: true, rss_mb: null, uptime: '', pid: null });
  const [boot, setBoot] = useState({ loading: true, latest_ms: null, avg_ms: null, min_ms: null, max_ms: null, count: 0 });

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const [mem, deploy] = await Promise.all([
          api.get('/health/memory'),
          // The legacy `/admin/deploy-metrics` was renamed to `/admin/deploy-status`
          // (iter341). We adapt the new response shape `{current, history, ...}`
          // back into the {latest_ms, avg_ms, min_ms, max_ms, count, records}
          // shape this badge expects, so the existing render code stays untouched.
          api.get('/admin/deploy-status?limit=10').catch(() => ({ data: null })),
        ]);
        if (!mounted) return;
        setState({ loading: false, rss_mb: mem.data.rss_mb, uptime: mem.data.uptime_human, pid: mem.data.pid });
        if (deploy.data) {
          const hist = (deploy.data.history || []).filter((h) => h && h.deploy_ready_ms != null);
          const vals = hist.map((h) => Number(h.deploy_ready_ms) || 0);
          const adapted = vals.length === 0 ? null : {
            latest_ms: deploy.data.current?.deploy_ready_ms ?? vals[0],
            avg_ms: Math.round(vals.reduce((s, v) => s + v, 0) / vals.length),
            min_ms: Math.min(...vals),
            max_ms: Math.max(...vals),
            count: vals.length,
            records: hist,
          };
          if (adapted) setBoot({ loading: false, ...adapted });
          else setBoot((b) => ({ ...b, loading: false }));
        } else setBoot((b) => ({ ...b, loading: false }));
      } catch {
        if (mounted) setState({ loading: false, rss_mb: null, uptime: 'unknown', pid: null });
      }
    };
    load();
    const id = setInterval(load, 60000);
    return () => { mounted = false; clearInterval(id); };
  }, []);

  const tone = state.loading
    ? 'muted'
    : state.rss_mb == null
      ? 'error'
      : state.rss_mb > 800 ? 'warning' : 'success';
  const fmtMs = (ms) => (ms == null ? '—' : ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`);
  const bootTone = boot.latest_ms == null
    ? 'muted'
    : boot.latest_ms > 10000 ? 'warning' : boot.latest_ms > 3000 ? 'info' : 'success';

  return (
    <Card
      className={`border ${tone === 'success' ? 'border-success/20 bg-success/5' : tone === 'warning' ? 'border-warning/30 bg-warning/5' : tone === 'error' ? 'border-error/20 bg-error/5' : 'border-border bg-muted/10'}`}
      data-testid="deployment-health-badge"
    >
      <CardContent className="p-2.5 flex items-center gap-3 text-xs flex-wrap">
        <div className={`w-2 h-2 rounded-full ${tone === 'success' ? 'bg-success animate-pulse' : tone === 'warning' ? 'bg-warning' : tone === 'error' ? 'bg-error' : 'bg-muted-foreground'}`} />
        <span className="font-semibold text-foreground">Backend</span>
        {state.loading ? (
          <span className="text-muted-foreground">Checking…</span>
        ) : state.rss_mb == null ? (
          <span className="text-error">Unreachable</span>
        ) : (
          <div className="flex items-center gap-4 text-muted-foreground">
            <span>uptime <strong className="text-foreground">{state.uptime}</strong></span>
            <span>memory <strong className={tone === 'warning' ? 'text-warning' : 'text-foreground'}>{state.rss_mb} MB</strong></span>
            <span className="text-[10px] font-mono opacity-60">pid {state.pid}</span>
          </div>
        )}
        {boot.latest_ms != null && (
          <div
            className={`ml-2 flex items-center gap-2 pl-3 border-l border-border ${bootTone === 'warning' ? 'text-warning' : bootTone === 'info' ? 'text-info' : 'text-muted-foreground'}`}
            title={`Last ${boot.count} cold-starts: avg ${fmtMs(boot.avg_ms)} · min ${fmtMs(boot.min_ms)} · max ${fmtMs(boot.max_ms)}`}
            data-testid="deploy-readiness-tile"
          >
            <span className="text-[10px] uppercase tracking-wider opacity-70">cold-start</span>
            <strong
              className={`tabular-nums ${bootTone === 'success' ? 'text-success' : bootTone === 'warning' ? 'text-warning' : bootTone === 'info' ? 'text-info' : 'text-foreground'}`}
              data-testid="deploy-latest-ms"
            >
              {fmtMs(boot.latest_ms)}
            </strong>
            {boot.records && boot.records.length >= 2 && (() => {
              const vals = [...boot.records].reverse().map((r) => r.deploy_ready_ms || 0);
              const max = Math.max(...vals, 1);
              const W = Math.min(60, vals.length * 5);
              const H = 14;
              const step = W / (vals.length - 1 || 1);
              const pts = vals.map((v, i) => `${(i * step).toFixed(1)},${(H - (v / max) * H).toFixed(1)}`).join(' ');
              return (
                <svg width={W} height={H} className="opacity-70" data-testid="deploy-sparkline">
                  <polyline fill="none" stroke="currentColor" strokeWidth="1.3" points={pts} />
                </svg>
              );
            })()}
            <span className="text-[10px] opacity-60">avg {fmtMs(boot.avg_ms)} ({boot.count})</span>
          </div>
        )}
        <span className="ml-auto text-[10px] text-muted-foreground">refreshes every 60s</span>
      </CardContent>
    </Card>
  );
}

export default DeploymentHealthBadge;
